// src/utils/activityOverheadEngine.js
// ─────────────────────────────────────────────────────────────────────────
// Shared activity-based overhead engine for the Project Profitability and
// Project Cost Ledger pages. Both pages used to maintain near-identical
// inline copies of this logic; in practice the copies drifted on subtle
// inputs (e.g. internal-project handling for op overhead) and the two
// pages produced different Dormancy totals on the same project. This
// module is the single source of truth.
//
// Two functions are exported:
//
//   getProjectMonths(project, options)
//     Returns the array of YYYY-MM keys representing the project's
//     "active period" — what the per-project monthly breakdown table
//     iterates over. Combines time entries, paid invoices, and project
//     metadata; extends to today for projects without an explicit end
//     date when extendToToday is true.
//
//   computeActivityOverheadEngine(inputs)
//     Returns { byProjectMonth, dormancyByProjectMonth, totalByProject,
//               dormancyTotalByProject, billableOfficeHours,
//               projOfficeHours, officeHoursByMonth, projLifespan,
//               projectMonthDebug, officeMonthReconciliation }
//
//     Operational overhead is allocated by hour share per office-month,
//     reserving DORMANCY_POOL_PCT of each pool for dormant alive
//     projects. Internal projects (BRIO General etc.) are excluded
//     from receiving operational overhead — their labour cost flows to
//     billable projects via the separate Internal OH redistribution
//     (see computeInternalCostRedistribution in ./projectLabour.js).
//     Internal projects ARE eligible for dormancy charging if they go
//     idle within their lifespan.
//
//     Dormancy is bounded by each project's actual lifespan (first to
//     last time entry, inclusive) so a project can never be charged
//     dormancy for office-months before it existed or after it ended.
// ─────────────────────────────────────────────────────────────────────────

const toNumericIdLocal = (id) => {
  if (typeof id === 'number') return id;
  if (typeof id === 'string' && /^-?\d+$/.test(id.trim())) return parseInt(id, 10);
  return id;
};

function monthRange(start, end) {
  if (!start || !end || start > end) return [];
  const result = [];
  const [sy, sm] = start.split('-').map(Number);
  const [ey, em] = end.split('-').map(Number);
  let y = sy, m = sm;
  while (y < ey || (y === ey && m <= em)) {
    result.push(`${y}-${String(m).padStart(2, '0')}`);
    m++; if (m > 12) { m = 1; y++; }
  }
  return result;
}

function todayYM() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// ─────────────────────────────────────────────────────────────────────────
// getProjectMonths
//
// Unified definition: the project's active period is the union of
//   - months with any time entry on the project
//   - months with a paid invoice on the project (if provided)
//   - the month containing the project's start date metadata
//   - the month containing the project's end date metadata, OR today
//     when extendToToday is true, no explicit end date is set, AND the
//     project is still active. Non-active projects (completed, on_hold,
//     cancelled, archived, …) clamp to last activity instead of today.
//
// Returns a sorted array of YYYY-MM keys, or selectedMonths verbatim
// when periodType !== 'total'.
// ─────────────────────────────────────────────────────────────────────────
export function getProjectMonths(project, {
  byProject,
  incomeByProjectMonth = null,
  periodType = 'total',
  selectedMonths = [],
  extendToToday = true,
} = {}) {
  if (periodType !== 'total') return selectedMonths;
  if (!project) return [];

  let earliest = null, latest = null;
  const setBound = ymKey => {
    if (!earliest || ymKey < earliest) earliest = ymKey;
    if (!latest || ymKey > latest) latest = ymKey;
  };

  // Time entries
  const projEntries = byProject?.[project.id];
  if (projEntries) {
    Object.values(projEntries).forEach(userMonths => {
      Object.keys(userMonths).forEach(setBound);
    });
  }

  // Paid invoices
  if (incomeByProjectMonth) {
    const projIncome = incomeByProjectMonth[project.id];
    if (projIncome) Object.keys(projIncome).forEach(setBound);
  }

  // Project metadata
  const startSrc = project.advanceStartDate || project.contractDate || project.startDate;
  if (startSrc) setBound(String(startSrc).substring(0, 7));
  const endSrc = project.completionDate || project.endDate;
  if (endSrc) {
    setBound(String(endSrc).substring(0, 7));
  } else if (extendToToday) {
    // Only pad to today for projects that are still active. Non-active
    // projects (completed, on_hold, cancelled, archived, …) clamp to the
    // latest activity month so the chart doesn't trail flat past the point
    // where work and billing stopped. If status is unset we assume active
    // to avoid hiding data on projects with missing metadata.
    const isActive = !project.status || project.status === 'active';
    if (isActive) setBound(todayYM());
  }

  if (!earliest) return [];
  return monthRange(earliest, latest);
}

// ─────────────────────────────────────────────────────────────────────────
// computeActivityOverheadEngine
//
// Inputs:
//   activeTimeEntries  full time-entry rows (date, projectId, userId, duration)
//   activeUsers        for office_location lookup
//   activeProjects     used to determine eligibility for dormancy charging
//   internalProjectIds Set of project IDs whose client is BRIO (or null)
//   monthlyOHIndex     { office: { 'YYYY-MM': amount } } — per-month override
//   yearlyOHIndex      { office: { 'YYYY': amount } } — yearly fallback ÷12
//   monthlyOfficeOverheads array of {office_location, month, amount_inr}
//   yearlyOfficeOverheads  array of {office_location, year, amount_in_inr}
//   DORMANCY_POOL_PCT  fraction reserved for dormancy (default 0.03)
//   buildDebug         when true, populate projectMonthDebug + reconciliation
//
// Dormancy is bounded by each project's actual time-entry lifespan.
// Operational overhead is distributed to billable projects only.
// ─────────────────────────────────────────────────────────────────────────
export function computeActivityOverheadEngine({
  activeTimeEntries = [],
  activeUsers = [],
  activeProjects = [],
  internalProjectIds = new Set(),
  monthlyOHIndex = {},
  yearlyOHIndex = {},
  monthlyOfficeOverheads = [],
  yearlyOfficeOverheads = [],
  DORMANCY_POOL_PCT = 0.03,
  // salaryGapByOfficeMonth[office][ym] = INR salary cost of contracted
  // hours that nobody logged to any project. Allocated to projects in
  // that office-month by hour share, mirroring the operational OH rule.
  // When totalOfficeHours is 0 the pool stays unallocated (residual).
  salaryGapByOfficeMonth = {},
  buildDebug = false,
  toNumericId = toNumericIdLocal,
} = {}) {
  // ─── 1. Index users by id for office lookup ──────────────────────────
  const userMap = {};
  activeUsers.forEach(u => { userMap[toNumericId(u.id)] = u; });

  const projectMap = {};
  activeProjects.forEach(p => { projectMap[toNumericId(p.id)] = p; });

  // ─── 2. projectsWithAnyTime — evidence-of-life check for dormancy ────
  const projectsWithAnyTime = new Set();
  activeTimeEntries.forEach(e => {
    if (e.projectId && (e.duration || 0) > 0) {
      projectsWithAnyTime.add(toNumericId(e.projectId));
    }
  });

  // ─── 3. Build hour aggregates from time entries ──────────────────────
  const officeHoursByMonth = {};      // { office: { ym: hours } }
  const billableOfficeHours = {};     // { office: { ym: hours } } excludes internal
  const projOfficeHours = {};         // { pid: { office: { ym: hours } } }
  const projTotalHoursByMonth = {};   // { pid: { ym: hours } } any-office
  const projLifespan = {};            // { pid: { first: ym, last: ym } }

  activeTimeEntries.forEach(e => {
    if (!e.date || !e.projectId) return;
    const ymKey = String(e.date).substring(0, 7);
    const hours = (e.duration || 0) / 3600;
    if (hours <= 0) return;
    const pid = toNumericId(e.projectId);
    const uid = toNumericId(e.userId);
    const office = (userMap[uid]?.officeLocation || 'NL') === 'IN' ? 'IN' : 'NL';

    if (!officeHoursByMonth[office]) officeHoursByMonth[office] = {};
    officeHoursByMonth[office][ymKey] = (officeHoursByMonth[office][ymKey] || 0) + hours;

    if (!internalProjectIds.has(pid)) {
      if (!billableOfficeHours[office]) billableOfficeHours[office] = {};
      billableOfficeHours[office][ymKey] = (billableOfficeHours[office][ymKey] || 0) + hours;
    }

    if (!projOfficeHours[pid]) projOfficeHours[pid] = {};
    if (!projOfficeHours[pid][office]) projOfficeHours[pid][office] = {};
    projOfficeHours[pid][office][ymKey] = (projOfficeHours[pid][office][ymKey] || 0) + hours;

    if (!projTotalHoursByMonth[pid]) projTotalHoursByMonth[pid] = {};
    projTotalHoursByMonth[pid][ymKey] = (projTotalHoursByMonth[pid][ymKey] || 0) + hours;

    if (!projLifespan[pid]) projLifespan[pid] = { first: ymKey, last: ymKey };
    else {
      if (ymKey < projLifespan[pid].first) projLifespan[pid].first = ymKey;
      if (ymKey > projLifespan[pid].last) projLifespan[pid].last = ymKey;
    }
  });

  // ─── 4. Pool lookup helper ───────────────────────────────────────────
  const getMonthlyPool = (office, ymKey) => {
    const monthly = monthlyOHIndex?.[office]?.[ymKey];
    if (typeof monthly === 'number') return { pool: monthly, source: 'monthly' };
    const yr = ymKey.substring(0, 4);
    const yearly = yearlyOHIndex?.[office]?.[yr];
    if (typeof yearly === 'number') return { pool: yearly / 12, source: 'yearly_derived' };
    return { pool: 0, source: 'missing' };
  };

  // ─── 5. Dormant project predicate (unified across both pages) ────────
  const getDormantProjects = (office, ymKey) => {
    const dormant = [];
    activeProjects.forEach(p => {
      const pid = toNumericId(p.id);
      const status = (p.status || 'active').toLowerCase();
      if (status === 'completed' || status === 'archived') return;
      // Project-level: any-office hours that month → not dormant
      if ((projTotalHoursByMonth[pid]?.[ymKey] || 0) > 0) return;
      // Must have evidence of life (any time entry anywhere)
      if (!projectsWithAnyTime.has(pid)) return;
      // Bound to actual project lifespan — no dormancy before first or
      // after last time entry. This is the rule that eliminated the
      // "dormancy charged before the project existed" bug from PR #228.
      const lifespan = projLifespan[pid];
      if (!lifespan) return;
      if (ymKey < lifespan.first || ymKey > lifespan.last) return;
      // Office association — only projects that have touched this office
      // share in this office's dormancy pool (otherwise NL dormancy
      // would be charged to a project that has never set foot in NL).
      if (projOfficeHours[pid]?.[office]) dormant.push(pid);
    });
    return dormant;
  };

  // ─── 6. First pass: operational overhead ─────────────────────────────
  const operationalResult = {};
  const dormancyResult = {};
  const allocatedOpByOfficeMonth = {};
  const allocatedDorByOfficeMonth = {};
  const projectMonthDebug = buildDebug ? [] : null;

  Object.entries(projOfficeHours).forEach(([pid, officeMap]) => {
    const numPid = typeof pid === 'number' ? pid : parseInt(pid, 10);
    const isInternal = internalProjectIds.has(numPid);
    operationalResult[pid] = {};
    Object.entries(officeMap).forEach(([office, monthHours]) => {
      Object.entries(monthHours).forEach(([ymKey, projHours]) => {
        const totalOfficeHours = officeHoursByMonth[office]?.[ymKey] || 0;
        const { pool: fullPool, source: poolSource } = getMonthlyPool(office, ymKey);
        const operationalPool = fullPool * (1 - DORMANCY_POOL_PCT);
        let overhead = 0;
        let reasonIfZero = null;

        if (isInternal) {
          reasonIfZero = 'internal_project';
        } else if (projHours <= 0) {
          reasonIfZero = 'no_project_hours';
        } else if (totalOfficeHours <= 0) {
          reasonIfZero = 'no_office_hours';
        } else if (fullPool <= 0) {
          reasonIfZero = poolSource === 'missing' ? 'monthly_data_missing' : 'no_overhead_pool';
        } else {
          overhead = operationalPool * (projHours / totalOfficeHours);
        }

        if (overhead > 0) {
          operationalResult[pid][ymKey] = (operationalResult[pid][ymKey] || 0) + overhead;
          if (!allocatedOpByOfficeMonth[office]) allocatedOpByOfficeMonth[office] = {};
          allocatedOpByOfficeMonth[office][ymKey] =
            (allocatedOpByOfficeMonth[office][ymKey] || 0) + overhead;
        }

        if (buildDebug) {
          const proj = projectMap[numPid];
          projectMonthDebug.push({
            projectId: pid,
            projectName: proj?.projectCode || proj?.name || `#${pid}`,
            officeKey: office,
            monthKey: ymKey,
            projectAllocableHours: projHours,
            totalAllocableOfficeHours: totalOfficeHours,
            projectHourShare: totalOfficeHours > 0 ? projHours / totalOfficeHours : 0,
            officeOverheadPool: fullPool,
            poolSource,
            type: isInternal ? 'internal' : 'operational',
            allocatedOperationalOverhead: overhead,
            reasonIfZero,
          });
        }
      });
    });
  });

  // ─── 6b. Salary gap allocation per office-month ──────────────────────
  // Mirrors the operational OH rule: per office-month, the office's gap
  // pool is allocated to non-internal projects by hour share. Same
  // denominator (totalOfficeHours) so the math is consistent. Internal
  // projects are excluded from the per-project view anyway.
  const salaryGapResult = {};
  const allocatedGapByOfficeMonth = {};
  Object.entries(projOfficeHours).forEach(([pid, officeMap]) => {
    const numPid = typeof pid === 'number' ? pid : parseInt(pid, 10);
    if (internalProjectIds.has(numPid)) return;
    salaryGapResult[pid] = {};
    Object.entries(officeMap).forEach(([office, monthHours]) => {
      Object.entries(monthHours).forEach(([ymKey, projHours]) => {
        const totalOfficeHours = officeHoursByMonth[office]?.[ymKey] || 0;
        const gapPool = Number(salaryGapByOfficeMonth?.[office]?.[ymKey]) || 0;
        if (gapPool <= 0 || totalOfficeHours <= 0 || projHours <= 0) return;
        const share = gapPool * (projHours / totalOfficeHours);
        if (share <= 0) return;
        salaryGapResult[pid][ymKey] = (salaryGapResult[pid][ymKey] || 0) + share;
        if (!allocatedGapByOfficeMonth[office]) allocatedGapByOfficeMonth[office] = {};
        allocatedGapByOfficeMonth[office][ymKey] =
          (allocatedGapByOfficeMonth[office][ymKey] || 0) + share;
      });
    });
  });

  // ─── 7. Second pass: dormancy carrying cost per office-month ─────────
  const allMonths = new Set();
  Object.values(officeHoursByMonth).forEach(mm => Object.keys(mm).forEach(ym => allMonths.add(ym)));
  monthlyOfficeOverheads.forEach(o => { if (o.month) allMonths.add(o.month); });
  Object.values(yearlyOHIndex).forEach(yearMap => {
    Object.keys(yearMap).forEach(yr => {
      for (let m = 1; m <= 12; m++) allMonths.add(`${yr}-${String(m).padStart(2, '0')}`);
    });
  });

  const allOffices = new Set([
    ...Object.keys(officeHoursByMonth),
    ...Object.keys(yearlyOHIndex || {}),
  ]);
  monthlyOfficeOverheads.forEach(o => {
    allOffices.add((o.officeLocation || o.office_location || 'NL') === 'IN' ? 'IN' : 'NL');
  });

  const sortedMonths = [...allMonths].sort();
  allOffices.forEach(office => {
    sortedMonths.forEach(ymKey => {
      const { pool: fullPool, source: poolSource } = getMonthlyPool(office, ymKey);
      if (fullPool <= 0) return;
      const dormancyPool = fullPool * DORMANCY_POOL_PCT;
      const dormantPids = getDormantProjects(office, ymKey);
      if (dormantPids.length === 0) return;
      const perProject = dormancyPool / dormantPids.length;
      dormantPids.forEach(pid => {
        if (!dormancyResult[pid]) dormancyResult[pid] = {};
        dormancyResult[pid][ymKey] = (dormancyResult[pid][ymKey] || 0) + perProject;
        if (!allocatedDorByOfficeMonth[office]) allocatedDorByOfficeMonth[office] = {};
        allocatedDorByOfficeMonth[office][ymKey] =
          (allocatedDorByOfficeMonth[office][ymKey] || 0) + perProject;

        if (buildDebug) {
          const proj = projectMap[pid];
          projectMonthDebug.push({
            projectId: pid,
            projectName: proj?.projectCode || proj?.name || `#${pid}`,
            officeKey: office,
            monthKey: ymKey,
            projectAllocableHours: 0,
            totalAllocableOfficeHours: officeHoursByMonth[office]?.[ymKey] || 0,
            projectHourShare: 0,
            officeOverheadPool: fullPool,
            poolSource,
            type: 'dormancy',
            allocatedOperationalOverhead: 0,
            allocatedDormancyCarryingCost: perProject,
            dormantProjectCount: dormantPids.length,
          });
        }
      });
    });
  });

  // ─── 8. Per-project totals ───────────────────────────────────────────
  const totalByProject = {};
  Object.entries(operationalResult).forEach(([pid, months]) => {
    totalByProject[pid] = Object.values(months).reduce((s, v) => s + v, 0);
  });
  const dormancyTotalByProject = {};
  Object.entries(dormancyResult).forEach(([pid, months]) => {
    dormancyTotalByProject[pid] = Object.values(months).reduce((s, v) => s + v, 0);
  });
  const salaryGapTotalByProject = {};
  Object.entries(salaryGapResult).forEach(([pid, months]) => {
    salaryGapTotalByProject[pid] = Object.values(months).reduce((s, v) => s + v, 0);
  });

  // ─── 9. Office-month reconciliation (pool ≟ allocated op + dormancy) ─
  const officeMonthReconciliation = buildDebug ? [] : null;
  if (buildDebug) {
    allOffices.forEach(office => {
      sortedMonths.forEach(ymKey => {
        const { pool: fullPool, source: poolSource } = getMonthlyPool(office, ymKey);
        if (fullPool <= 0) return;
        const allocOp = allocatedOpByOfficeMonth[office]?.[ymKey] || 0;
        const allocDor = allocatedDorByOfficeMonth[office]?.[ymKey] || 0;
        const totalAllocableOfficeHours = officeHoursByMonth[office]?.[ymKey] || 0;
        let activeProjectCount = 0;
        Object.values(projOfficeHours).forEach(officeMap => {
          if ((officeMap[office]?.[ymKey] || 0) > 0) activeProjectCount += 1;
        });
        const dormantProjectCount = getDormantProjects(office, ymKey).length;
        officeMonthReconciliation.push({
          officeKey: office,
          monthKey: ymKey,
          officeOverheadPool: fullPool,
          poolSource,
          operationalPool: fullPool * (1 - DORMANCY_POOL_PCT),
          dormancyPool: fullPool * DORMANCY_POOL_PCT,
          totalAllocableOfficeHours,
          activeProjectCount,
          dormantProjectCount,
          allocatedOperationalOverhead: allocOp,
          allocatedDormancyCarryingCost: allocDor,
          unallocatedOverhead: fullPool - allocOp - allocDor,
          reconciliationDelta: fullPool - allocOp - allocDor,
        });
      });
    });
  }

  return {
    byProjectMonth: operationalResult,
    dormancyByProjectMonth: dormancyResult,
    salaryGapByProjectMonth: salaryGapResult,
    salaryGapTotalByProject,
    allocatedGapByOfficeMonth,
    totalByProject,
    dormancyTotalByProject,
    billableOfficeHours,
    projOfficeHours,
    officeHoursByMonth,
    projLifespan,
    projectMonthDebug,
    officeMonthReconciliation,
  };
}
